/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { BuilderUserContext } from '@gh/builder-shared';
import { ScheduleType } from '@gh/builder-shared-data';
import { FormState, FormStateBuilder, whenCreateMode } from '@gh/common-shared';
import { OfficeData } from '@gh/common-shared-data';
import { sliceDataByQuery } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  FormMode,
  getControl,
  WaitingService,
} from '@gh/core-ui';
import { map$ } from '@gh/rx/operators';

import { eq, toString } from 'lodash';
import { SchedulingScheduleTemplateBusinessService } from '../../resources/scheduling-schedule-template-business.service';

import {
  ScheduleTemplateCreateDialogItem,
  ScheduleTemplateCreateType,
  ScheduleTemplateDialogConfig,
} from '../../resources/scheduling-schedule-template.model';

@Component({
  standalone: false,
  selector: 'gh-create-schedule-template-dialog',
  templateUrl: 'scheduling-schedule-template-details-dialog.component.html',
  styleUrls: ['scheduling-schedule-template-details-dialog.component.scss'],
})
export class SchedulingScheduleTemplateDetailsDialogComponent implements OnInit, DialogInput<ScheduleTemplateDialogConfig> {
  input: ScheduleTemplateDialogConfig;
  form: FormState<ScheduleTemplateCreateDialogItem>;
  scheduleTemplatesType: ScheduleType;

  scheduleTemplateCreateType = ScheduleTemplateCreateType;

  constructor(private dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private business: SchedulingScheduleTemplateBusinessService,
              private waitingService: WaitingService,
              private formStateBuilder: FormStateBuilder,
              private userContext: BuilderUserContext) {
  }

  ngOnInit(): void {
    this.scheduleTemplatesType = this.input.type;

    this.form = this.formStateBuilder.buildPlain({
      mode: FormMode.CreateMode,
      enabled: whenCreateMode(),
      value: {
        createType: ScheduleTemplateCreateType.Scratch,
      },
      children: {
        createType: { required: true },
        scheduleTemplate: { enabled: false, required: true },
      },
    });
  }

  onTypeChange($event: any): void {
    const scheduleTemplateControl = getControl(this.form.group, 'scheduleTemplate');
    if ($event.value === ScheduleTemplateCreateType.Scratch) {
      scheduleTemplateControl.disable();
    } else {
      scheduleTemplateControl.enable();
    }
  }

  onCreate(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      ...this.form.submitValue,
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  loadScheduleTemplates($event: AutocompleteLoadMoreEvent): void {
    const office = <OfficeData>this.userContext.office;
    $event.load(this.business.findAllByOffice(office.id, {
      searchString: toString($event.query.filter),
      showConstructionTemplates: eq(this.scheduleTemplatesType, ScheduleType.Construction),
      showSalesTemplates: eq(this.scheduleTemplatesType, ScheduleType.Sales),
      showPreConstructionsTemplates: eq(this.scheduleTemplatesType, ScheduleType.PreConstruction),
      showMaintenanceTemplates: eq(this.scheduleTemplatesType, ScheduleType.Maintenance),
    }).pipe(map$((templates) => sliceDataByQuery(templates, $event.query))));
  }
}
